import {Component, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Observable, Subject} from 'rxjs';
import {IStore} from '../../../stores/store';
import {StoreAutocompleteComponent} from '../store-autocomplete/store-autocomplete.component';
import {NotificationService} from '../../notification.service';
import {Router} from '@angular/router';
import {FormsService} from '../../../visits/forms/forms.service';
import {marker} from '@biesbjerg/ngx-translate-extract-marker';
import {ConfirmDialogService} from '../../confirm-dialog.service';
import {TranslateService} from '@ngx-translate/core';
import {STEPPER_GLOBAL_OPTIONS} from '@angular/cdk/stepper';
import {CalendarService} from '../../../calendar/calendar.service';
import {HelpersService} from '../../helpers.service';
import {IScheduledVisit} from '../../../calendar/calendar';
import {TimeUtilsService} from '../../time-utils.service';
import {TimezonesService} from '../../../core/timezones.service';
import {isNil} from 'lodash-es';
import {StoresService} from '../../../stores/services/stores.service';
import {IdName} from '../../common-interfaces';
import {GroupsService} from '../../../users/groups/groups.service';
import {LabelingService} from '../../labeling.service';


@Component({
    selector: 'store-selection-dialog',
    templateUrl: './start-store-visit-dialog.component.html',
    styleUrls: ['./start-store-visit-dialog.component.scss'],
    providers: [{
            provide: STEPPER_GLOBAL_OPTIONS, useValue: { showError: true },
        }],
    standalone: false
})
export class StartStoreVisitDialogComponent implements OnInit, OnDestroy {

  private destroyed$ = new Subject<void>();

  public form: FormGroup;

  @ViewChild('storeAutocomplete', { static: true }) public readonly storeAutocomplete: StoreAutocompleteComponent;
  selectedStoreId?: number;
  selectedStoreTimeZone?: string;
  accountIds: number[];
  eventTypes: IdName[];
  timeIn = new Date();
  groups: IdName[];
  selectedStep: number;
  firstFormGroup: FormGroup;
  secondFormGroup: FormGroup;
  pageTitle = this.translateService.stream(marker('New Store Visit'));

  isFormProvided: boolean;
  forms: IdName[];
  storeSelectionLabel: Observable<any>;

  constructor(
    public dialogRef: MatDialogRef<StartStoreVisitDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { formId?: number, storeId?: number},
    private fb: FormBuilder,
    private router: Router,
    private notificationService: NotificationService,
    private confirmDialogService: ConfirmDialogService,
    private translateService: TranslateService,
    private formsService: FormsService,
    private storesService: StoresService,
    private calendarService: CalendarService,
    private groupsService: GroupsService,
    private helpersService: HelpersService,
    private timeUtilsService: TimeUtilsService,
    private timezonesService: TimezonesService,
    public labelingService: LabelingService,
  ) {
    this.isFormProvided = ! isNil(this.data.formId);
    this.storeSelectionLabel = labelingService.getStoreSentenceLabel(marker('Store Selection'), marker('Selection'));
  }

  ngOnInit(): void {
    if (this.isFormProvided) {
      this.formsService.getFormDefinition(this.data.formId).subscribe(
        result => this.accountIds = result.publishedAccount,
      );
    } else {
      this.selectedStoreId = this.data.storeId;

      this.formsService.getStoreFormsFiltered(this.selectedStoreId).subscribe(
        myForms => {
          this.forms = myForms;
        },
      );

      this.storesService.getStore(this.selectedStoreId).subscribe(
        store => {
          this.selectedStoreTimeZone = this.timezonesService.getTimezoneJavaIdFromId(store.timeZoneId);
          this.setUpDateTimesFormDefaultValues();
        },
      );

    }

    this.calendarService.getStoreVisitEventTypes().subscribe(
      result => {
        this.eventTypes = result.map(eventType => {
          return {id: eventType.id, name: eventType.storeVisitEventType};
        });
        this.secondFormGroup.get('eventType').patchValue(this.eventTypes[0].id.toString());
        this.secondFormGroup.updateValueAndValidity();
      },
    );

    this.groupsService.getMyGroups().subscribe(result => this.groups = result);

    this.form = this.fb.group({});

    if (this.isFormProvided) {
      this.firstFormGroup = this.fb.group({
        store: ['', Validators.required],
      });
    } else {
      this.firstFormGroup = this.fb.group({
        form: ['', Validators.required],
      });
    }


    this.secondFormGroup = this.fb.group({
      eventType: [ '', [Validators.required]],
      startDate: [ null, [Validators.required]],
      startTime: [ null, [Validators.required]],
      endDate: [ null, [Validators.required]],
      endTime: [ null, [Validators.required]],
      notes: [''],
      group: [''],
    });
    this.form.addControl('firstGroup', this.firstFormGroup);
    this.form.addControl('secondGroup', this.secondFormGroup);

    this.setUpDateTimesFormDefaultValues();
  }

  confirmUpdate(): void {
    const scheduledVisit: IScheduledVisit = {
      storeId: this.selectedStoreId,
      storeVisitEventTypeId: +this.secondFormGroup.get('eventType').value,
      startTime:  this.helpersService.generateUnixDateFromDateAndTimeAndTimezone(
        this.secondFormGroup.get('startDate').value,
        this.secondFormGroup.get('startTime').value,
        this.selectedStoreTimeZone),
      endTime: this.helpersService.generateUnixDateFromDateAndTimeAndTimezone(
        this.secondFormGroup.get('endDate').value,
        this.secondFormGroup.get('endTime').value,
        this.selectedStoreTimeZone),
      notes: this.secondFormGroup.get('notes').value,
      groupId: this.secondFormGroup.get('group').value !== '' ? +this.secondFormGroup.get('group').value : null,
    };

    this.calendarService.createScheduledVisit(scheduledVisit).subscribe(
      (result) => {
        this.dialogRef.close(false);

        let formId: number;
        if (this.isFormProvided) {
          formId = this.data.formId;
        } else {
          formId = this.firstFormGroup.get('form').value;
        }

        this.router.navigate(['/visits/forms/:storeFormId/startStoreVisit/:storeId',
            {storeFormId: formId, storeId: this.selectedStoreId},
          ],
          {
            queryParams: {storeVisitId: result.scheduledVisitId},
          });
      },
    );

  }


  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onStoreSelected(store: IStore) {
    if (this.firstFormGroup !== undefined) {
      this.firstFormGroup.get('store').setValue(store.id);
      this.firstFormGroup.updateValueAndValidity();
      this.selectedStoreId = store.id;
      this.selectedStoreTimeZone = this.timezonesService.getTimezoneJavaIdFromId(store.timeZoneId);

      this.setUpDateTimesFormDefaultValues();
    }
  }

  stepSelected(event: any) {
    this.selectedStep = event.selectedIndex;
  }

  cancel(): void {
    if (this.form.dirty === true) {
      this.confirmDialogService.openConfirmDialog(
        this.translateService.stream(marker('Any unsaved changes will be lost.')),
      ).subscribe(response => {
        if (response) {
          this.close();
        }
      });
    } else {
      this.close();
    }
  }

  close() {
    this.removeValidations();
    this.form.reset();
    // this.cancelEv.emit(false);
    this.dialogRef.close(false);
  }

  removeValidations() {
    // removes annoying error validation messages when a form is canceled
    Object.keys(this.firstFormGroup.controls).forEach(key => {
      this.firstFormGroup.get(key).clearValidators();
      this.firstFormGroup.get(key).updateValueAndValidity();
    });
  }

  private setUpDateTimesFormDefaultValues() {
    const defaultStart = this.timeUtilsService.roundUpToMinutesMultipleOf(new Date(), 5);
    const defaultEnd = this.timeUtilsService.addMinutes(defaultStart, 30);
    const defaultStartTime = this.helpersService.extractTimeForEdit(defaultStart, this.selectedStoreTimeZone);
    const defaultEndTime = this.helpersService.extractTimeForEdit(defaultEnd, this.selectedStoreTimeZone);

    this.secondFormGroup.get('startDate').patchValue(new Date());
    this.secondFormGroup.get('endDate').patchValue(new Date());
    this.secondFormGroup.get('startTime').patchValue(defaultStartTime);
    this.secondFormGroup.get('endTime').patchValue(defaultEndTime);
  }
}
